import { useEffect, useState } from 'react';
import { api } from './api';

// The current user's effective permissions (from GET /auth/me/permissions),
// fetched once and cached in-module so every component agrees without refetching.
//
// Unlike entitlements (module visibility, which fails OPEN), permissions gate
// real actions and therefore fail CLOSED: until they load — and if the fetch
// fails — `can()` returns false, so a permission-gated button/menu stays hidden
// rather than briefly exposing something the user may not be allowed to do. The
// backend is still the real enforcement; this just keeps the UI honest.
let cache: Set<string> | null = null;
let inflight: Promise<Set<string>> | null = null;
const listeners = new Set<(p: Set<string>) => void>();

export async function fetchPermissions(force = false): Promise<Set<string>> {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = api
    .get('/auth/me/permissions')
    .then((res) => {
      cache = new Set<string>(res.data?.permissions || []);
      listeners.forEach((l) => l(cache!));
      return cache;
    })
    .catch(() => {
      // Fail closed (empty set) but do NOT cache it — the next call refetches, so
      // a transient error / pre-login 401 doesn't poison the cache for the session.
      cache = null;
      return new Set<string>();
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function clearPermissions() {
  cache = null;
  inflight = null;
}

// Fail closed: null (not yet loaded) → false.
export function hasPermission(perm: string, p: Set<string> | null = cache): boolean {
  return p !== null && p.has(perm);
}

export function usePermissions() {
  const [perms, setPerms] = useState<Set<string> | null>(cache);
  useEffect(() => {
    let active = true;
    const onUpdate = (p: Set<string>) => { if (active) setPerms(p); };
    listeners.add(onUpdate);
    fetchPermissions().then((p) => { if (active) setPerms(p); });
    return () => { active = false; listeners.delete(onUpdate); };
  }, []);
  return {
    perms,
    can: (perm: string) => hasPermission(perm, perms),
    loading: perms === null,
  };
}
